/**
 * Conversions between the builder chain (certs + AND/OR connectors), the stored expression tree,
 * and the bracketed display text. AND binds tighter than OR.
 */
import { Connector, ExpressionNode } from './types';

export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Builder chain → tree. Splits at every OR; each segment becomes HAS or AND[HAS…]. */
export function chainToTree(certs: string[], connectors: Connector[]): ExpressionNode {
  const segments: string[][] = [[certs[0]]];
  for (let i = 1; i < certs.length; i++) {
    if (connectors[i - 1] === 'AND') segments[segments.length - 1].push(certs[i]);
    else segments.push([certs[i]]);
  }
  const nodes: ExpressionNode[] = segments.map((s) =>
    s.length === 1
      ? { type: 'HAS', cert: s[0] }
      : { type: 'AND', children: s.map((c) => ({ type: 'HAS', cert: c }) as ExpressionNode) }
  );
  return nodes.length === 1 ? nodes[0] : { type: 'OR', children: nodes };
}

const certOf = (node: ExpressionNode): string => (node.type === 'HAS' ? node.cert : '');

/** Tree → builder chain (Edit). Supports the v1 canonical shape (OR of ANDs of HAS). */
export function treeToChain(node: ExpressionNode): { certs: string[]; connectors: Connector[] } {
  const groups = node.type === 'OR' ? node.children : [node];
  const certs: string[] = [];
  const connectors: Connector[] = [];
  groups.forEach((g) => {
    const inGroup = g.type === 'AND' ? g.children.map(certOf) : [certOf(g)];
    inGroup.forEach((c, ci) => {
      if (certs.length) connectors.push(ci === 0 ? 'OR' : 'AND');
      certs.push(c);
    });
  });
  return { certs, connectors };
}

/** Tree → display text with brackets (never stored). */
export function renderExpression(node: ExpressionNode, parent?: 'AND' | 'OR'): string {
  if (node.type === 'HAS') return node.cert;
  const text = node.children.map((c) => renderExpression(c, node.type)).join(` ${node.type} `);
  return parent === 'OR' && node.type === 'AND' ? `(${text})` : text;
}

/** Parses a stored expression; returns null if it isn't valid JSON. */
export function parseExpression(expression: string): ExpressionNode | null {
  try {
    return JSON.parse(expression) as ExpressionNode;
  } catch {
    return null;
  }
}

/** Client-side validation mirroring the server. Returns an error message or null. */
export function validateChain(certs: string[], connectors: Connector[]): string | null {
  if (!certs.length) return 'Add at least one Certificate ID';
  if (connectors.slice(0, certs.length - 1).some((c) => !c)) {
    return 'Please select AND or OR between every pair of certificates';
  }
  const bad = certs.find((c) => !UUID_RE.test(c));
  if (bad) return `Invalid certificate ID: ${bad}`;
  // Duplicates are allowed ACROSS AND-groups ("at least 2 of 3") but not within one group,
  // and no two groups may be identical.
  const tree = chainToTree(certs, connectors);
  const groups = tree.type === 'OR' ? tree.children : [tree];
  const sets = groups.map((g) =>
    (g.type === 'AND' ? g.children.map(certOf) : [certOf(g)]).map((c) => c.toLowerCase())
  );
  if (sets.some((s) => new Set(s).size !== s.length)) {
    return 'A certificate cannot be repeated inside the same AND group';
  }
  const keys = sets.map((s) => [...s].sort().join('|'));
  if (new Set(keys).size !== keys.length) return 'Two groups of certificates are identical';
  return null;
}
